import { Horizon, rpc, StrKey } from "@stellar/stellar-sdk";
import { Buffer } from "buffer";

import { KeybanClientBase, KeybanClientConfig, MetadataConfig } from "~/client";
import { StellarAccount } from "~/stellar/account";
import { StellarSigner } from "~/stellar/signer";

/**
 * Stellar network client that provisions a {@link StellarAccount} using the Keyban EDDSA service.
 * Persists/loads the EDDSA client share, derives public key/address, and wires Horizon and RPC.
 * @example
 * import { KeybanClient, KeybanNetwork } from "@keyban/sdk-base";
 *
 * const client = new KeybanClient({
 *   apiUrl: "https://api.prod.keyban.io",
 *   appId: "YOUR_APP_ID",
 *   network: KeybanNetwork.StellarTestnet,
 * });
 * const account = await client.initialize();
 * console.log(account.address);
 */
export class StellarClient extends KeybanClientBase {
  #metadataConfig: Promise<MetadataConfig>;

  constructor(
    config: KeybanClientConfig,
    metadataConfig?: Promise<MetadataConfig>,
  ) {
    super(config, metadataConfig);
    this.#metadataConfig =
      metadataConfig ?? Promise.reject(new Error("metadataConfig is required"));
  }

  /**
   * Initialize the Stellar account for the configured network.
   * Requires `horizonUrl` in network metadata.
   * @returns A ready-to-use {@link StellarAccount} bound to Stellar RPC and Horizon.
   * @throws {Error} If `horizonUrl` is missing from network config.
   * @example
   * const account = await stellarClient.initialize();
   * console.log(account.publicKey);
   */
  async initialize(): Promise<StellarAccount> {
    const key = `eddsa:${this.network}`;

    let clientShare = await this.clientShareProvider.get(key);
    if (!clientShare) {
      clientShare = await this.api.eddsa.dkg();
      await this.clientShareProvider.set(key, clientShare);
    }

    const [config, publicKey] = await Promise.all([
      this.#metadataConfig,
      this.api.eddsa.publicKey(clientShare),
    ]);
    if (!config.network.horizonUrl) {
      throw new Error("Missing required horizonUrl in network config");
    }

    const signer = new StellarSigner(this.api, clientShare, publicKey);

    const stellarPubKey = StrKey.encodeEd25519PublicKey(
      Buffer.from(publicKey, "hex"),
    );

    return new StellarAccount(
      this.api,
      new rpc.Server(config.network.rpcUrl),
      new Horizon.Server(config.network.horizonUrl),
      signer,
      stellarPubKey,
    );
  }
}
